/**
 * The respondent's capability token for their own survey session.
 *
 * `survey_session.id` is a serial, and the session routes are unauthenticated by design — a
 * respondent never logs in. The token is what distinguishes "the respondent whose session this is"
 * from "someone counting integers", so it has to travel on every session-scoped call.
 *
 * Kept per session id rather than one global slot: a respondent can legitimately hold sessions on
 * several surveys at once, and a single slot would let the newest silently evict the others.
 */

/** Header the backend reads (`SESSION_TOKEN_HEADER` in routes_fastapi/survey.py). */
export const SESSION_TOKEN_HEADER = 'X-Session-Token';

/**
 * Query-parameter fallback for the same value. Needed because the liveness ping goes out through
 * `navigator.sendBeacon`, which cannot set a header — and that ping is the last thing a closing
 * tab sends, so it cannot be dropped.
 */
export const SESSION_TOKEN_PARAM = 'session_token';

const STORAGE_PREFIX = 'survey_session_token_';

const storageKey = (sessionId: number): string => `${STORAGE_PREFIX}${sessionId}`;

export const readSessionToken = (sessionId: number): string | null => {
  try {
    return localStorage.getItem(storageKey(sessionId));
  } catch {
    // private mode / quota: the survey still works, it just cannot prove itself after a reload
    return null;
  }
};

export const rememberSessionToken = (sessionId: number, token?: string | null): void => {
  if (!sessionId || !token) return;
  try {
    localStorage.setItem(storageKey(sessionId), String(token));
  } catch {
    // as above — never let a storage failure stop a respondent answering
  }
};

export const forgetSessionToken = (sessionId: number): void => {
  try {
    localStorage.removeItem(storageKey(sessionId));
  } catch {
    /* nothing to do */
  }
};

/**
 * Pull `(id, session_token)` out of whatever envelope a route used and remember it.
 *
 * Deliberately shape-tolerant: the session row is returned bare by some routes, under `session` by
 * others and under `session_data` by the create call. Coupling this to one shape is the mistake
 * that made `getSessionById` return null for every session it was handed.
 */
export const rememberSessionFromPayload = (payload: unknown): void => {
  if (!payload || typeof payload !== 'object') return;
  const envelope = payload as Record<string, unknown>;
  for (const candidate of [envelope, envelope.session, envelope.session_data]) {
    if (!candidate || typeof candidate !== 'object') continue;
    const row = candidate as Record<string, unknown>;
    if (typeof row.id === 'number' && row.session_token) {
      rememberSessionToken(row.id, String(row.session_token));
    }
  }
};

/** The session id in a survey-session URL, or null when the URL is not session-scoped. */
const SESSION_URL_PATTERN = /\/api\/surveys\/sessions\/(\d+)/;

export const sessionIdFromUrl = (url: string): number | null => {
  const match = SESSION_URL_PATTERN.exec(url);
  return match ? Number(match[1]) : null;
};

/** The same URL carrying the token as a query parameter, for the beacon path only. */
export const withSessionToken = (url: string): string => {
  const sessionId = sessionIdFromUrl(url);
  const token = sessionId === null ? null : readSessionToken(sessionId);
  if (!token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}${SESSION_TOKEN_PARAM}=${encodeURIComponent(token)}`;
};
